import React, { useState, useEffect } from 'react';
import { Link, useStaticQuery, graphql } from 'gatsby';
import moment from 'moment';

import Asset from '@src/components/Asset';
import Button from '@src/components/buttons/Button';

export default function ProjectList() {
    const {
        tags: { nodes: tags },
        pages: { nodes: pages },
    } = useStaticQuery(
        graphql`
            {
                tags: allCraftTagInterface {
                    nodes {
                        title
                        groupHandle
                    }
                }
                pages: allCraftProjectsAndEventsDefaultEntry(
                    filter: { enabled: { eq: true } }
                    sort: { fields: eventDate, order: DESC }
                ) {
                    nodes {
                        remoteId
                        typeHandle
                        level
                        uri
                        ... on Craft_projectsAndEvents_default_Entry {
                            title
                            url
                            eventDate
                            plainText
                            projectAndEventTags {
                                title
                            }
                            singleImage {
                                ... on Craft_images_Asset {
                                    url
                                    title
                                    imageFile {
                                        childImageSharp {
                                            gatsbyImageData(layout: CONSTRAINED)
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        `
    );

    const GRID_SET = 9;

    const filteredTags = tags.filter(tag => tag.groupHandle !== 'productTags');
    let projects = pages;
    projects = projects.filter(proj => proj.level !== 1);

    const years = [];

    const [when, setWhen] = useState('All');
    const [tag, setTag] = useState('All');
    const [list, setList] = useState([...projects.slice(0, 9)]);
    const [loadMore, setLoadMore] = useState(false);
    const [hasMore, setHasMore] = useState(projects.length > 9);

    const shuffleArr = arr => {
        let currentIndex = arr.length;
        let randomIndex;

        while (currentIndex !== 0) {
            randomIndex = Math.floor(Math.random() * currentIndex);
            // eslint-disable-next-line no-plusplus
            currentIndex--;

            // eslint-disable-next-line no-param-reassign
            [arr[currentIndex], arr[randomIndex]] = [arr[randomIndex], arr[currentIndex]];
        }

        return arr;
    };

    const shuffleList = () => {
        let shuffled = shuffleArr(projects);
        shuffled = shuffled.slice(0, 9);
        setList([...shuffled]);
    };

    const handleFilter = e => {
        setTag(e.target.value);
    };

    const handleTime = e => {
        setWhen(e.target.value);
    };

    const handleLoadMore = () => {
        setLoadMore(true);
    };

    useEffect(() => {
        if (tag === 'All' && when === 'All') {
            setList([...projects.slice(0, GRID_SET)]);
        } else if (tag !== 'All' && when !== 'All') {
            let filteredPosts = projects
                .filter(obj => obj.projectAndEventTags.length > 0)
                .filter(obj => {
                    const objTagTitles = obj.projectAndEventTags.map(objTag => objTag.title);
                    return objTagTitles.includes(tag);
                });
            filteredPosts = filteredPosts.filter(obj => moment(obj.eventDate).format('Y') === when);
            setList(filteredPosts);
        } else if (tag !== 'All') {
            const filteredPosts = projects
                .filter(obj => obj.projectAndEventTags.length > 0)
                .filter(obj => {
                    const objTagTitles = obj.projectAndEventTags.map(objTag => objTag.title);
                    return objTagTitles.includes(tag);
                });
            setList(filteredPosts);
        } else if (when !== 'All') {
            const filteredPosts = projects.filter(
                obj => moment(obj.eventDate).format('Y') === when
            );
            setList(filteredPosts);
        }
    }, [tag, when]);

    useEffect(() => {
        if (loadMore && hasMore) {
            const currentLength = list.length;
            if (tag === 'All' && when === 'All') {
                const isMore = currentLength < projects.length;
                const nextResults = isMore
                    ? projects.slice(currentLength, currentLength + GRID_SET)
                    : [];
                setList([...list, ...nextResults]);
                setLoadMore(false);
            } else if (tag !== 'All' && when !== 'All') {
                let filteredPosts = projects
                    .filter(obj => obj.projectAndEventTags.length > 0)
                    .filter(obj => {
                        const objTagTitles = obj.projectAndEventTags.map(objTag => objTag.title);
                        return objTagTitles.includes(tag);
                    });
                filteredPosts = filteredPosts.filter(
                    obj => moment(obj.eventDate).format('Y') === when
                );
                const isMore = currentLength < filteredPosts.length;
                const nextResults = isMore
                    ? filteredPosts.slice(currentLength, currentLength + GRID_SET)
                    : [];
                setList([...list, ...nextResults]);
                setLoadMore(false);
            } else if (tag !== 'All') {
                const filteredPosts = projects
                    .filter(obj => obj.projectAndEventTags.length > 0)
                    .filter(obj => {
                        const objTagTitles = obj.projectAndEventTags.map(objTag => objTag.title);
                        return objTagTitles.includes(tag);
                    });
                const isMore = currentLength < filteredPosts.length;
                const nextResults = isMore
                    ? filteredPosts.slice(currentLength, currentLength + GRID_SET)
                    : [];
                setList([...list, ...nextResults]);
                setLoadMore(false);
            } else if (when !== 'All') {
                const filteredPosts = projects.filter(
                    obj => moment(obj.eventDate).format('Y') === when
                );
                const isMore = currentLength < filteredPosts.length;
                const nextResults = isMore
                    ? filteredPosts.slice(currentLength, currentLength + GRID_SET)
                    : [];
                setList([...list, ...nextResults]);
                setLoadMore(false);
            }
        }
    }, [list, loadMore, hasMore]);

    useEffect(() => {
        if (tag === 'All' && when === 'All') {
            if (list.length < projects.length) {
                setHasMore(true);
            } else {
                setHasMore(false);
            }
        } else if (tag !== 'All' && when !== 'All') {
            let filteredPosts = projects
                .filter(obj => obj.projectAndEventTags.length > 0)
                .filter(obj => {
                    const objTagTitles = obj.projectAndEventTags.map(objTag => objTag.title);
                    return objTagTitles.includes(tag);
                });
            filteredPosts = filteredPosts.filter(obj => moment(obj.eventDate).format('Y') === when);
            if (list.length < filteredPosts.length) {
                setHasMore(true);
            } else {
                setHasMore(false);
            }
        } else if (tag !== 'All') {
            const filteredPosts = projects
                .filter(obj => obj.projectAndEventTags.length > 0)
                .filter(obj => {
                    const objTagTitles = obj.projectAndEventTags.map(objTag => objTag.title);
                    return objTagTitles.includes(tag);
                });
            if (list.length < filteredPosts.length) {
                setHasMore(true);
            } else {
                setHasMore(false);
            }
        } else if (when !== 'All') {
            const filteredPosts = projects.filter(
                obj => moment(obj.eventDate).format('Y') === when
            );
            if (list.length < filteredPosts.length) {
                setHasMore(true);
            } else {
                setHasMore(false);
            }
        }
    }, [list]);

    const postsEl = list.map(entry => {
        const date = moment(entry.eventDate).format('MMM D, Y');
        const location = entry.plainText;
        return (
            <li key={entry.title} className="relative list-style-none list-wrapper">
                <Link to={`/${entry.uri}`}>
                    {entry.singleImage[0] ? (
                        <div className="mb-24px">
                            <Asset
                                border
                                data={entry.singleImage[0].imageFile}
                                title={entry.singleImage[0].title}
                            />
                        </div>
                    ) : null}

                    <h3 className="h3 mb-8px">{entry.title}</h3>
                    {date ? <p>{date}</p> : null}
                    {location ? <p>{location}</p> : null}
                </Link>
            </li>
        );
    });

    let yearOptions = projects.forEach(project => {
        const year = moment(project.eventDate).format('Y');
        if (!years.includes(year)) {
            years.push(year);
        }
        return years;
    });
    yearOptions = years.map(year => (
        <option key={`year-${year}`} value={year}>
            {year}
        </option>
    ));

    return (
        <div>
            <div className="flex flex-wrap mb-32px">
                <div style={{ marginRight: 36 }} className="flex flex-wrap items-center mb-8px">
                    <h4 style={{ marginRight: 8 }} className="h4 m-0">
                        When:
                    </h4>
                    <div className="flex relative arrow-down">
                        <select onChange={handleTime} className="button button--select h4 m-0">
                            <option value="All">All</option>
                            {yearOptions}
                        </select>
                    </div>
                </div>
                <div className="flex flex-wrap items-center mb-8px" style={{ marginRight: 36 }}>
                    <h4 style={{ marginRight: 8 }} className="h4 m-0">
                        Type:
                    </h4>
                    <div className="flex relative arrow-down">
                        <select onChange={handleFilter} className="button button--select h4 m-0">
                            <option value="All">All</option>
                            {filteredTags.map(item => (
                                <option key={item.title} value={item.title}>
                                    {item.title}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="flex flex-wrap items-center mb-8px">
                    <button
                        type="button"
                        onClick={() => shuffleList()}
                        className="button"
                        style={{ minWidth: 240 }}>
                        <h4 className="h4 m-0">Surprise me</h4>
                    </button>
                </div>
            </div>

            <div className="grid grid--3 mb-64px">{postsEl}</div>
            {hasMore ? (
                <Button
                    large
                    classes="button--full mb-16px"
                    copy="Load More"
                    onClick={handleLoadMore}
                />
            ) : null}
        </div>
    );
}
